import { Injectable } from '@angular/core';
import { FileTypeService } from '@fi-shared/fi-shared-services/file-type.service';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { MediaBankFileModel } from './media-bank-file.model';
import { ModuleType } from '@fi-shared/types/module-type';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { CropImageService } from '@fi-app/utilities/crop-image/crop-image.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { FiImageSize, FileType } from '@fi-shared/types';
import { OperationOverlayLargeNg4Service } from '@fi-app/utilities/operation-overlay-large/operation-overlay-large.service';
import { MediaBankViewContainerComponent } from './media-bank-view-container/media-bank-view-container.component';
import { Observable, Subject, Subscription } from 'rxjs';
import { FiMediaBankService, IMediaBankFile } from '@fi-shared/media-bank';
import { IMediaBankFileUploadErrorItem } from '@fi-shared/media-bank/media-bank';
import { FiFileUpload2Service } from '@fi-app/shared/fi-insert-media/fi-file-upload2.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { ILightWeightTag } from '@fi-shared/fi-tag2/fi-tag2';

@Injectable()
export class MediaBankService {
    constructor(
        private fileTypeService: FileTypeService,
        private fiBackend: FiBackendService,
        private mediaBankHelperService: MediaBankHelperService,
        private cropImageService: CropImageService,
        private fiLoadingService: LoadingService,
        private fileUpload2Service: FiFileUpload2Service,
        private operationOverlayLargeService: OperationOverlayLargeNg4Service,
        private mediaBankSharedService: FiMediaBankService,
        private mediaStorageService: FiMediaStorageService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    getMediaBankFileTagsByUserAccessRights(): Promise<ILightWeightTag[]> {
        return this.fiBackend.fetchData<ILightWeightTag[]>(
            `Core/Tag/GetModuleTagsByUserAccessRights/${ModuleType.MediaBank}`,
        );
    }

    GetMediaBankFileTags() {
        return this.mediaBankSharedService.getMediaBankFileTags();
    }

    SaveMediaBankFile(mediaBankFile) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<number>('MediaBank/MediaBank/SaveMediaBankFile', mediaBankFile),
        );
    }

    SaveMediaBankFiles(mediaBankFiles: MediaBankFileModel[]) {
        return this.fiBackend.fetchDataPost('MediaBank/MediaBank/SaveMediaBankFiles', mediaBankFiles);
    }

    replaceFile(
        fileToUpload: File,
        inProcessFile: MediaBankFileModel,
        onError: (uploadError: IMediaBankFileUploadErrorItem) => void,
    ) {
        inProcessFile.PercentageLoaded = 0;
        inProcessFile.PercentageProcessed = 0;

        const replaceFileUrl = `MediaBank/FileUpload/ReplaceFileV2/${inProcessFile.OrigId}`;
        return this.fiLoadingService
            .trackPromise(
                this.fileUpload2Service.uploadFiles({
                    files: [fileToUpload],
                    onUploadStarted: (uploadItem) => {
                        uploadItem.percentageLoaded$.subscribe((v) => {
                            inProcessFile.PercentageLoaded = inProcessFile.PercentageProcessed = v;
                        });
                    },
                    onUploadError: (uploadError) => onError(uploadError),
                    skipSaveMediaBankOnBackend: true,
                    url: replaceFileUrl,
                }),
            )
            .then((mediaBankFiles) => {
                inProcessFile.Initialize(mediaBankFiles[0], this.fiFormatDateService, {
                    preserveDescriptions: true,
                    preserveSettings: true,
                });
                inProcessFile.Name = mediaBankFiles[0].Name;
            });
    }

    /*ReplaceFile(fileToUpload: File,
                inProcessFile: MediaBankFileModel,
                onError: (uploadError: IMediaBankFileUploadErrorItem) => void)
    {
        return new Promise<void>((resolve, reject) => {
            if (fileToUpload.size > this.getFileUploadLimit(fileToUpload.name))
            {
                onError({ mediaBankFile: inProcessFile, errorCode: StatusCodeType.FileUploadLimitExceed })
                reject();
                return;
            }

            this.GenerateThumb( fileToUpload, inProcessFile);

            if (this.vimeoService.shouldUploadToVimeo(inProcessFile)) {
                this.vimeoService.uploadToVimeo(fileToUpload, {
                    onProgress: evt => inProcessFile.PercentageLoaded = evt.loaded !== evt.total ? parseInt(<any>(100.0 * evt.loaded / evt.total)) : 99
                }).then(vimeoId => {
                    inProcessFile.PhysicalFileName = vimeoId;
                    inProcessFile.PercentageLoaded = 100;
                    inProcessFile.PercentageProcessed = 100;
                    resolve();
                }, (err) => {
                    onError({ mediaBankFile: inProcessFile, errorCode: err.status })
                    reject();
                });

            } else {
                this.fileUploadService.upload(fileToUpload, undefined, {
                    url:this.fiBackend.getRequestUrl(`mediabank/fileupload/replacefile/${inProcessFile.Id}`),
                    onProgress: evt => inProcessFile.PercentageLoaded = evt.loaded !== evt.total ? parseInt(<any>(100.0 * evt.loaded / evt.total)) : 99
                }).then(data => {
                    inProcessFile.PercentageLoaded = 100;
                    inProcessFile.PercentageProcessed = 100;
                    inProcessFile.Initialize(data[0]);
                    resolve();
                }).catch(err => {
                    onError({ mediaBankFile: inProcessFile, errorCode: err.status })
                    reject();
                });
            }
        });
    };*/

    generateThumb(file: File) {
        const promise = new Promise<{
            data: string;
        }>((resolve, reject) => {
            const fileType = this.fileTypeService.getFileType(file);
            if (fileType === FileType.Image) {
                const reader = new FileReader();
                reader.onload = (event: any) => {
                    this.cropImageService.fixOrientation(event.target.result).then((result) => {
                        resolve({
                            data: result,
                        });
                    });
                };
                reader.onerror = () => reject();
                reader.readAsDataURL(file);
            } else {
                reject();
            }
        });
        return promise;
    }

    /*GenerateThumb(fileToUpload:File, inProcessFile: MediaBankFileModel) {
        const defer = new Defer();
        inProcessFile.FileType = this.fileTypeService.getFileType(fileToUpload);
        if (inProcessFile.FileType == FileType.Image) {
            const reader = new FileReader();
            reader.onload = (event: any) => {
                this.cropImageService.fixOrientation(event.target.result).then(function(result) {
                    inProcessFile.Url = result;
                    defer.resolve();
                });
            };

            if (fileToUpload) {
                reader.readAsDataURL(fileToUpload);
            } else {
                defer.reject();
            }
        } else {
            defer.resolve();
        }

        return defer.promise;
    }*/

    ForceDeleteMediaBankFile(id) {
        return this.fiBackend.fetchData(`MediaBank/MediaBank/ForceDeleteMediaBankFile/${id}`);
    }

    DeleteMediaBankFiles(mediaBankFileIdsToDelete: number[]) {
        return this.fiBackend.fetchDataPost('MediaBank/MediaBank/DeleteMediaBankFiles', mediaBankFileIdsToDelete);
    }

    // fontawesome v4
    GetMediaBankFileIcon(fileType) {
        return this.mediaBankHelperService.getMediaBankFileIcon(fileType);
    }

    GetMediaBankFiles(mediaBankFileParameterApiModel): Promise<MediaBankFileModel[]> {
        return this.fiBackend.fetchDataPost('MediaBank/MediaBank/GetMediaBankFiles', mediaBankFileParameterApiModel);
    }

    getMediaBankImages(mediaBankModel: IMediaBankFile[]): MediaBankFileModel[] {
        const result: MediaBankFileModel[] = [];
        mediaBankModel.forEach((item) => {
            const mediaBankFile = new MediaBankFileModel(this.fiFormatDateService, item);
            mediaBankFile.Image = this.getThumbnailImage(mediaBankFile);
            mediaBankFile.ImageLarge = this.getImage(mediaBankFile, FiImageSize.Small);
            result.push(mediaBankFile);
        });
        return result;
    }

    private getThumbnailImage(mediaBankFile: MediaBankFileModel): string {
        switch (mediaBankFile.FileType) {
            case FileType.Image:
            case FileType.Pdf:
                return this.mediaStorageService.getMediaFileUrl(mediaBankFile, { size: FiImageSize.Thumbnail });
        }
    }

    private getImage(mediaBankFile: MediaBankFileModel, size: FiImageSize): string {
        if (mediaBankFile.FileType === FileType.Image)
            return this.mediaStorageService.getMediaFileUrl(mediaBankFile, { size: size });
        return this.getThumbnailImage(mediaBankFile);
    }

    GetMediaBankFilesCount(mediaBankFileParameterApiModel) {
        return this.fiBackend.fetchDataPost<number>(
            'MediaBank/MediaBank/GetMediaBankFilesCount',
            mediaBankFileParameterApiModel,
        );
    }

    GetLightWeightObjectsForLocalSearch(mediaBankFileParameterApiModel): Promise<ILightWeightObjectCollection> {
        return this.mediaBankSharedService.getLightWeightObjectsForLocalSearch(mediaBankFileParameterApiModel);
    }

    RestoreMediaBankFiles(restoreIds: number[]) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<void>('MediaBank/MediaBank/RestoreMediaBankFiles', restoreIds),
        );
    }

    markAllMediaBankFilesAsSeen() {
        return this.fiBackend.fetchDataPost('MediaBank/MediaBank/MarkAllMediaBankFilesAsSeen');
    }

    MarkMediaBankFileAsVisited(id: number) {
        return this.fiBackend.fetchDataPost('MediaBank/MediaBank/MarkMediaBankFileAsVisited/' + id, undefined, {
            supressErrorMessage: true,
        });
    }

    GetMediaBankFileUsages(ids: number[]) {
        return this.fiBackend.fetchDataPost<any[]>('MediaBank/MediaBank/GetMediaBankFileUsages', ids);
    }

    openMediaViewDialog(mediaId: number, params) {
        const finishLoading = this.fiLoadingService.startLoading();
        this.mediaBankSharedService.getMediaBankFile(mediaId, true).then((data) => {
            const mediaFile = new MediaBankFileModel(this.fiFormatDateService);
            mediaFile.Initialize(data, this.fiFormatDateService);

            this.operationOverlayLargeService.openOperationOverlayLargeDialog(
                MediaBankViewContainerComponent,
                {
                    mediaBankFile: mediaFile,
                    isFromMediaBankModalDialog: true,
                },
                params,
            );
            finishLoading();
        }, finishLoading);
    }

    private _reloadMediaBankList$ = new Subject<void>();
    doReloadMediaBankList$() {
        this._reloadMediaBankList$.next();
    }

    get reloadMediaBankList$(): Observable<any> {
        return this._reloadMediaBankList$.asObservable();
    }
}
